import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { useGetActiveAnnouncements } from 'modules/announcement/api/queries'
import { getDismissedIds, markDismissed } from 'modules/announcement/utils/dismissed'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Checkbox, IconButton, Stack, Typography } from '@mui/material'

import closeFill from '@iconify/icons-eva/close-fill'
import { Icon } from '@iconify/react'

import { DialogAnimate } from 'components/animate'

import { Announcement } from '../api/types'

// ----------------------------------------------------------------------

const CARD_WIDTH = 420

interface AnnouncementCardProps {
  announcement: Announcement
  onClose: () => void
}

// The "ไม่ต้องแสดงอีก" checkbox state lives HERE, not in the parent that
// renders DialogAnimate. DialogAnimate builds its Paper from an inline
// PaperComponent that MUI remounts whenever DialogAnimate re-renders — a
// remount replays the fade-in animation. Keeping `checked` in this leaf means
// ticking it re-renders only this card, so the popup stays put; DialogAnimate
// re-renders (and the fade replays) only when the parent advances to the next
// announcement, which is the intended moment for a new fade.
function AnnouncementCard({ announcement, onClose }: AnnouncementCardProps) {
  const { t } = useTranslation('common')
  const [checked, setChecked] = useState(false)

  const handleImageClick = () => {
    const url = announcement.destination_url
    if (url && /^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener')
    }
  }

  const handleClose = () => {
    if (checked) {
      markDismissed(announcement.id)
    }
    onClose()
  }

  return (
    <Box sx={{ position: 'relative', width: CARD_WIDTH, maxWidth: '90vw' }}>
      <Box
        component="img"
        src={announcement.image_url}
        alt={announcement.title}
        onClick={handleImageClick}
        sx={{
          width: '100%',
          display: 'block',
          borderRadius: '24px',
          cursor: announcement.destination_url ? 'pointer' : 'default',
        }}
      />

      <IconButton
        onClick={handleClose}
        aria-label="close"
        sx={{
          position: 'absolute',
          top: -16,
          right: -16,
          width: 44,
          height: 44,
          bgcolor: 'common.white',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.24)',
          '&:hover': { bgcolor: 'common.white' },
        }}
      >
        <Icon icon={closeFill} width={22} height={22} />
      </IconButton>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={0.5}
        sx={{ mt: '26px' }}
      >
        <Checkbox
          checked={checked}
          onChange={(event) => setChecked(event.target.checked)}
          sx={{
            color: 'common.white',
            '&.Mui-checked': { color: 'common.white' },
          }}
        />
        <Typography sx={{ color: 'common.white', fontSize: 14 }}>{t('announcement.dont.show.again')}</Typography>
      </Stack>
    </Box>
  )
}

export default function SystemAnnouncementDialog() {
  const { data, isLoading, isError } = useGetActiveAnnouncements()
  const [index, setIndex] = useState(0)
  const { trackEvent } = useAnalytics()

  // API already returns newest-first — do not re-sort.
  const queue = useMemo(() => {
    if (!data) return []
    const dismissedIds = getDismissedIds()
    return data.filter((announcement) => !dismissedIds.has(announcement.id))
  }, [data])

  const current = queue[index]
  const currentId = current?.id

  useEffect(() => {
    if (!currentId) return
    trackEvent(AnalyticsEvents.bannerImpression, {
      banner_type: 'announcement',
      banner_id: currentId,
    })
  }, [currentId, trackEvent])

  if (isLoading || isError || !current) {
    return null
  }

  return (
    <DialogAnimate
      open
      fullWidth={false}
      maxWidth={false}
      disableEscapeKeyDown
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.55)' } }}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          margin: 0,
          overflow: 'visible',
          maxWidth: 'none',
        },
      }}
    >
      {/* key by id so advancing resets the card (and its checkbox) cleanly. */}
      <AnnouncementCard
        key={current.id}
        announcement={current}
        onClose={() => setIndex((prev) => prev + 1)}
      />
    </DialogAnimate>
  )
}
